import { contractSearch } from "@/features/contracts/searchState";
import { analyticsSearch } from "@/features/analytics/searchState";
import { activityTabFromHint, type ActivityTab } from "@/features/activity/activityTabs";
/**
 * Code-based TanStack Router tree.
 *
 * The root route is the authenticated shell ({@link RootLayout}); every feature
 * page hangs off it. Paths line up with `ROUTES`/`RESOURCES` in `@/config` so
 * the URL, the nav item and the RBAC resource never drift.
 */
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
  Link,
} from "@tanstack/react-router";
import { RootLayout } from "./RootLayout";
import { RouteError, RoutePending } from "./RouteStatus";
import { OverviewPage } from "@/features/overview/OverviewPage";

/**
 * The landing page (`OverviewPage`) is bundled eagerly so the first authenticated
 * paint is instant. Every other page — and its heavy deps (tremor charts, the
 * xyflow graph, yjs collaboration, the pipeline studio) — is split into its own
 * chunk via `lazyRouteComponent` and fetched on navigation (`defaultPreload:
 * "intent"` warms it on hover/focus). This keeps the initial bundle small.
 */
const lazyPage = (
  importer: () => Promise<Record<string, React.ComponentType>>,
  name: string,
) => lazyRouteComponent(importer, name);

/** A search param as a non-empty string, or `undefined`. Used by `validateSearch`. */
const str = (value: unknown): string | undefined =>
  typeof value === "string" && value ? value : undefined;

/** A search param as a positive finite number, or `undefined`. */
const num = (value: unknown): number | undefined => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const GettingStartedPage = lazyPage(() => import("@/features/overview/GettingStartedPage"), "GettingStartedPage");
const TransactionsPage = lazyPage(() => import("@/features/transactions/TransactionsPage"), "TransactionsPage");
const TransactionDetailPage = lazyPage(() => import("@/features/transactions/TransactionDetailPage"), "TransactionDetailPage");
const AddressesPage = lazyPage(() => import("@/features/addresses/AddressesPage"), "AddressesPage");
const AddressDetailPage = lazyPage(() => import("@/features/addresses/AddressDetailPage"), "AddressDetailPage");
const CatalogPage = lazyPage(() => import("@/features/catalog/CatalogPage"), "CatalogPage");
const PoolDetailPage = lazyPage(() => import("@/features/pools/PoolDetailPage"), "PoolDetailPage");
const EntityDetailPage = lazyPage(() => import("@/features/catalog/CatalogDetailRoutes"), "EntityDetailPage");
const TagDetailPage = lazyPage(() => import("@/features/catalog/CatalogDetailRoutes"), "TagDetailPage");
const LabelDetailPage = lazyPage(() => import("@/features/catalog/CatalogDetailRoutes"), "LabelDetailPage");
const GroupDetailPage = lazyPage(() => import("@/features/catalog/CatalogDetailRoutes"), "GroupDetailPage");
const GraphsPage = lazyPage(() => import("@/features/graphs/GraphsPage"), "GraphsPage");
const InvestigationsPage = lazyPage(() => import("@/features/investigations/InvestigationsPage"), "InvestigationsPage");
const InvestigationDetailPage = lazyPage(() => import("@/features/investigations/InvestigationDetailPage"), "InvestigationDetailPage");
const AskAiPage = lazyPage(() => import("@/features/ask-ai/AskAiPage"), "AskAiPage");
const StatsPage = lazyPage(() => import("@/features/stats/StatsPage"), "StatsPage");
const AnalyticsPage = lazyPage(() => import("@/features/analytics/AnalyticsPage"), "AnalyticsPage");
const ExplorerPage = lazyPage(() => import("@/features/analytics/ExplorerPage"), "ExplorerPage");
const ProtocolOverridesPage = lazyPage(() => import("@/features/protocols/ProtocolOverridesPage"), "ProtocolOverridesPage");
const DashboardViewerPage = lazyPage(() => import("@/features/analytics/DashboardViewerPage"), "DashboardViewerPage");
const ActivityPage = lazyPage(() => import("@/features/activity/ActivityPage"), "ActivityPage");
const PipelinesPage = lazyPage(() => import("@/features/pipelines/PipelinesPage"), "PipelinesPage");
const PipelineBuilderPage = lazyPage(() => import("@/features/pipelines/PipelineBuilderPage"), "PipelineBuilderPage");
const RunsPage = lazyPage(() => import("@/features/runs/RunsPage"), "RunsPage");
const TracersPage = lazyPage(() => import("@/features/tracers/TracersPage"), "TracersPage");
const GroupingRulesPage = lazyPage(
  () => import("@/features/grouping/GroupingRulesPage"),
  "GroupingRulesPage",
);
const AlertsPage = lazyPage(() => import("@/features/alerts/AlertsPage"), "AlertsPage");
const IntegrationsPage = lazyPage(() => import("@/features/integrations/IntegrationsPage"), "IntegrationsPage");
const WatchesPage = lazyPage(() => import("@/features/watches/WatchesPage"), "WatchesPage");
const WatchDetailPage = lazyPage(
  () => import("@/features/watches/WatchDetailPage"),
  "WatchDetailPage",
);
const SituationsPage = lazyPage(() => import("@/features/watches/SituationsPage"), "SituationsPage");
const ActorsPage = lazyPage(() => import("@/features/actors/ActorsPage"), "ActorsPage");
const PredictionMarketsPage = lazyPage(
  () => import("@/features/prediction/PredictionMarketsPage"),
  "PredictionMarketsPage",
);
const FlowsPage = lazyPage(() => import("@/features/flows/FlowsPage"), "FlowsPage");
const ContractsPage = lazyPage(() => import("@/features/contracts/ContractsPage"), "ContractsPage");
const ContractDetailPage = lazyPage(() => import("@/features/contracts/ContractDetailPage"), "ContractDetailPage");
const WalletPage = lazyPage(() => import("@/features/wallet/WalletPage"), "WalletPage");
const TeamPage = lazyPage(() => import("@/features/team/TeamPage"), "TeamPage");
const AcceptTeamInvitePage = lazyPage(() => import("@/features/team/AcceptTeamInvitePage"), "AcceptTeamInvitePage");
const SettingsLayout = lazyPage(() => import("@/features/settings/SettingsLayout"), "SettingsLayout");
const SettingsPage = lazyPage(() => import("@/features/settings/SettingsPage"), "SettingsPage");
const AccessPage = lazyPage(() => import("@/features/settings/AccessPage"), "AccessPage");
const WorkspaceSettingsPage = lazyPage(() => import("@/features/settings/WorkspacePage"), "WorkspacePage");
const DefaultsPage = lazyPage(() => import("@/features/settings/DefaultsPage"), "DefaultsPage");
const ApisPage = lazyPage(() => import("@/features/settings/ApisPage"), "ApisPage");
const ApiKeysPage = lazyPage(() => import("@/features/settings/ApiKeysPage"), "ApiKeysPage");
const BillingPage = lazyPage(() => import("@/features/settings/BillingPage"), "BillingPage");
const ExportsPage = lazyPage(() => import("@/features/settings/ExportsPage"), "ExportsPage");
const ProfilePage = lazyPage(() => import("@/features/profile/ProfilePage"), "ProfilePage");

function NotFound(): React.ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <h1 className="text-2xl font-semibold text-foreground">Page not found</h1>
      <p className="mt-1 text-sm text-muted-foreground">The page you&apos;re looking for doesn&apos;t exist.</p>
      <Link to="/" className="mt-4 text-sm font-medium text-primary hover:underline">
        Back to overview
      </Link>
    </div>
  );
}

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
});

// Settings is a small nested SITE: `SettingsLayout` renders the shared header +
// the grouped left nav (`features/settings/settingsNav.ts`), and the child route
// paints into its `<Outlet />`. One page per subject rather than nine cards on
// the index, so each is deep-linkable and preloads on intent — which is what
// lets a scope pill point at `/settings/defaults#scope-default-visibility`.
//
// Every path here must be in `ROUTES` and named by the settings nav or its
// exemption list; `settingsNav.test.ts` reads this file and asserts both ways.
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: SettingsLayout,
});
const settingsRouteTree = settingsRoute.addChildren([
  createRoute({ getParentRoute: () => settingsRoute, path: "/", component: SettingsPage }),
  createRoute({ getParentRoute: () => settingsRoute, path: "/access", component: AccessPage }),
  createRoute({ getParentRoute: () => settingsRoute, path: "/workspace", component: WorkspaceSettingsPage }),
  createRoute({ getParentRoute: () => settingsRoute, path: "/defaults", component: DefaultsPage }),
  createRoute({ getParentRoute: () => settingsRoute, path: "/apis", component: ApisPage }),
  createRoute({ getParentRoute: () => settingsRoute, path: "/api-keys", component: ApiKeysPage }),
  createRoute({ getParentRoute: () => settingsRoute, path: "/billing", component: BillingPage }),
  createRoute({ getParentRoute: () => settingsRoute, path: "/exports", component: ExportsPage }),
]);

const routes = [
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/",
    component: OverviewPage,
    // `?guide=1` forces the Auth0-style onboarding guide on the landing page
    // even for a set-up / previously-dismissed tenant.
    validateSearch: (search: Record<string, unknown>): { guide?: boolean } => ({
      guide:
        search.guide === 1 ||
        search.guide === "1" ||
        search.guide === true ||
        search.guide === "true"
          ? true
          : undefined,
    }),
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/getting-started", component: GettingStartedPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/transactions",
    component: TransactionsPage,
    // The hand-off from a case: `?address=` (one, or several comma-separated)
    // narrows the list to a participant, `?chain=` scopes that lookup to one
    // chain, and `?subject=` says which of several is being read. The route
    // took no params at all, so "Open in explorer" on an investigation could
    // only ever land on the unfiltered tenant feed — see `./subjectFilter`.
    //
    // `?inspect=` is the open side panel (`features/inspector/inspectorParam`).
    // Declared here for the reason a validated route always must: an undeclared
    // param is STRIPPED, so without this line opening the panel would rewrite
    // the URL and the router would immediately drop what was written.
    validateSearch: (
      search: Record<string, unknown>,
    ): { address?: string; chain?: string; subject?: string; inspect?: string } => {
      const text = (value: unknown): string | undefined =>
        typeof value === "string" && value.trim() ? value.trim() : undefined;
      return {
        address: text(search.address),
        chain: text(search.chain),
        subject: text(search.subject),
        inspect: text(search.inspect),
      };
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/transactions/$id",
    component: TransactionDetailPage,
    // `?chain=` names the chain for a BARE hash. A hash is only unique per
    // chain, so `/transactions/0x…` on its own is not a reference to a
    // transaction; the page probes which chains carry it and links each one
    // back here with this param. Declared so the value is typed and validated
    // at the route rather than read loosely off the URL.
    //
    // `?tab=` opens one of the activity lenses (semantic / flow / call / events
    // / internal / raw). "Look at the events on this transaction" is the most
    // ordinary thing one analyst says to another, and the link used to open
    // whichever lens the page picked for itself. An unknown value falls through
    // to that default rather than 404ing.
    //
    // `?inspect=` is the open side panel; see the note on `/transactions`.
    validateSearch: (
      search: Record<string, unknown>,
    ): { chain?: string; tab?: string; inspect?: string } => ({
      chain:
        typeof search.chain === "string" && search.chain.trim()
          ? search.chain.trim()
          : undefined,
      tab: str(search.tab),
      inspect: str(search.inspect),
    }),
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/addresses", component: AddressesPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/addresses/$id",
    component: AddressDetailPage,
    // `?chain=` preselects a chain on the multi-chain detail page (e.g. when a
    // transaction's from/to drilldown knows which chain it came from).
    //
    // `?tab=` opens one lens directly, same convention as the catalog detail
    // routes below. The scanner needs it: a reader clicking a flagged wallet is
    // asking what it did, and landing them on Overview to hunt for the Conduct
    // tab loses the question they arrived with. Unknown values fall through to
    // the page default rather than 404ing (the page still maps the old
    // `behavior` spelling, since those links are already out there).
    validateSearch: (
      search: Record<string, unknown>,
    ): { chain?: string; tab?: string; actorScope?: string } => ({
      actorScope: str(search.actorScope),
      chain: typeof search.chain === "string" && search.chain ? search.chain : undefined,
      tab: typeof search.tab === "string" && search.tab ? search.tab : undefined,
    }),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/catalog",
    component: CatalogPage,
    // `?view=review` opens the review queue — the discoveries held for a person
    // instead of being written — beside the catalog it governs. `?status=` /
    // `?kind=` are the queue's filters, carried only on that view, so another
    // surface can deep-link the lane it means (the address page's "queued for
    // verification" notice points at pending address proposals). Unknown values
    // fall through to the page's defaults rather than 404ing.
    validateSearch: (
      search: Record<string, unknown>,
    ): { view?: "review"; status?: string; kind?: string } => {
      const view = search.view === "review" ? "review" : undefined;
      return {
        view,
        status: view ? str(search.status) : undefined,
        kind: view ? str(search.kind) : undefined,
      };
    },
  }),
  // The review queue's old address. It was its own page and rail entry; it is
  // the catalog's Review view now. Redirected rather than deleted, with the lane
  // preserved, because `/proposals?status=…&kind=…` links are already out there.
  //
  // What the view lists is TENANT-SCOPED for this app: seed-mgmt-api returns the
  // caller's org-tenant's proposals (plus their own user-scoped ones), answers
  // 404 for an item outside that tenancy and 403 for a decision on one. Global
  // seed-run and reconciler proposals are reviewed in the admin portal. The
  // dashboard only reaches that API at all since the authorizer whitelisted it
  // for this app — before, every read 403'd with PERM002, Admins included.
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/proposals",
    validateSearch: (search: Record<string, unknown>): { status?: string; kind?: string } => ({
      status: str(search.status),
      kind: str(search.kind),
    }),
    beforeLoad: ({ search }) => {
      throw redirect({
        to: "/catalog",
        search: { view: "review", status: search.status, kind: search.kind },
        replace: true,
      });
    },
  }),
  // `/entities` and `/groups` were the two pages the catalog replaced. Redirected
  // rather than deleted: they are in bookmarks, in links pasted into cases, and
  // in the onboarding checklist. Their DETAIL routes keep their own paths — an
  // entity's page is about that entity, not about the catalog.
  ...["/entities", "/groups"].map((from) =>
    createRoute({
      getParentRoute: () => rootRoute,
      path: from,
      beforeLoad: () => {
        throw redirect({ to: "/catalog", replace: true });
      },
    }),
  ),
  // The four catalog detail paths share one page; `?tab=` keeps the open lens in
  // the URL so "what this tag is attached to" can be pasted into a case note and
  // open on the same reading. Unknown values fall through to the page's default
  // rather than 404ing — a stale bookmark should still land on the item.
  ...(
    [
      ["/entities/$id", EntityDetailPage],
      ["/groups/$id", GroupDetailPage],
      ["/tags/$id", TagDetailPage],
      ["/labels/$id", LabelDetailPage],
    ] as const
  ).map(([path, component]) =>
    createRoute({
      getParentRoute: () => rootRoute,
      path,
      component,
      validateSearch: (search: Record<string, unknown>): { tab?: string; situationScope?: string } => ({
        situationScope: typeof search.situationScope === "string" ? search.situationScope : undefined,
        tab: typeof search.tab === "string" && search.tab ? search.tab : undefined,
      }),
    }),
  ),
  createRoute({ getParentRoute: () => rootRoute, path: "/graphs", component: GraphsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/investigations", component: InvestigationsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/investigations/$id", component: InvestigationDetailPage,
    validateSearch: (search: Record<string, unknown>): { relationship?: string } => ({ relationship: typeof search.relationship === "string" ? search.relationship : undefined }),
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/ask-ai", component: AskAiPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/stats", component: StatsPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/analytics", component: AnalyticsPage }),
  // Static segment declared before the `$dashboardId` param so it wins the match.
  createRoute({ getParentRoute: () => rootRoute, path: "/analytics/explorer", component: ExplorerPage, validateSearch: analyticsSearch }),
  // `?protocol=` names the selected definition. The page held the selection in
  // component state over a catalogue of HUNDREDS of protocols, so the answer to
  // "how does this platform decode Across?" was not a link — it was a set of
  // directions, and a reload dropped it. It is also the target the transaction
  // page's protocol badges need in order to be links at all.
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/analytics/protocols",
    component: ProtocolOverridesPage,
    // `?policy=<domain|knob path>` opens the confidence policy ON that row.
    // Every surface that reports what the policy DID offers a way to it, and
    // "it" is a card of four tables and forty-odd settings at the bottom of a
    // page listing hundreds of protocols — so the link has to name the row, not
    // just the page.
    validateSearch: (search: Record<string, unknown>): { protocol?: string; q?: string; chains?: string[]; group?: string; policy?: string; policyScope?: string } => ({
      protocol: str(search.protocol),
      q: str(search.q),
      chains: Array.isArray(search.chains) ? search.chains.filter((c): c is string => typeof c === "string" && Boolean(c)) : undefined,
      group: search.group === "chain" ? "chain" : undefined,
      policy: str(search.policy),
      policyScope: str(search.policyScope),
    }),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/analytics/$dashboardId",
    component: DashboardViewerPage,
    // `?var_<name>=<value>` seeds the board's template variables, so a board
    // narrowed to one token or pool is a URL — pasteable into a case note, and
    // linkable from the page the subject belongs to. Without it an embedded
    // panel's "open the full board" link landed on the unfiltered board, which
    // reads as though the numbers had changed.
    validateSearch: analyticsSearch,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/activity",
    component: ActivityPage,
    // `?tab=` names which of the three lanes opens. Each lane reads a different
    // (and, for Audit, a very heavy) table, so "look at what ran" and "look at
    // what it cost" are things people link to and reload — and without the
    // param both landed on Notifications. Declared as the page's own union so a
    // stale or hand-typed value falls back rather than rendering nothing.
    validateSearch: (search: Record<string, unknown>): { tab?: ActivityTab } => ({
      tab: activityTabFromHint(search.tab),
    }),
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/pipelines", component: PipelinesPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/pipelines/$id",
    component: PipelineBuilderPage,
    // `/pipelines/new?from=<id>` starts a new pipeline as a copy of an existing
    // one (a system preset or the caller's own). Nothing is written until Save.
    // `tab` opens the builder on a side-panel tab — the transactions page's
    // "Create custom pipeline" lands on Config, where "Filter indexes" is.
    validateSearch: (search: Record<string, unknown>): { from?: string; tab?: string } => ({
      from: str(search.from),
      tab: str(search.tab),
    }),
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/runs",
    component: RunsPage,
    // Two prefills arrive here, and they are told apart by which bounds are set.
    // `?chain=&tx=` re-decodes one transaction (the transaction detail page's
    // decode-quality escape hatch). `?chain=&from=&to=` — optionally `&address=`
    // to narrow to one contract — opens a block-range backfill, which is how a
    // watch asks for the history it was created too late to have seen.
    //
    // `?run=` is the selected run, so a run can be linked to and survives a
    // reload — it used to be component state, and every run looked the same
    // from the address bar.
    validateSearch: (
      search: Record<string, unknown>,
    //
    // `?pipeline=` NAMES the pipeline to prefill, which the generic re-decode
    // link deliberately does not: there, choosing one is the point. It is for
    // the links that arrive with the answer already — "this call did not decode,
    // run Forensic" — where making the reader find that pipeline themselves is
    // asking them to know something the page already knew.
    ): {
      run?: string;
      chain?: string;
      tx?: string;
      from?: string;
      to?: string;
      address?: string;
      pipeline?: string;
    } => {
      return {
        run: str(search.run),
        chain: str(search.chain),
        tx: str(search.tx),
        from: str(search.from),
        to: str(search.to),
        address: str(search.address),
        pipeline: str(search.pipeline),
      };
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/tracers",
    component: TracersPage,
    validateSearch: (search: Record<string, unknown>): { run?: string; inspect?: string } => ({
      run: str(search.run),
      inspect: str(search.inspect),
    }),
  }),
  // A tracer IS a thing you send someone — "look at what this run found". The
  // page held its selection in component state, so there was nothing to paste
  // into a case, a ticket or a message, and a reload dropped it.
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/tracers/$id",
    component: TracersPage,
    validateSearch: (search: Record<string, unknown>): { run?: string; inspect?: string } => ({
      run: str(search.run),
      inspect: str(search.inspect),
    }),
  }),
  // A sibling of /pipelines rather than a tab on /runs: these are
  // configuration, Runs is the operational record of what ran.
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/grouping-rules",
    component: GroupingRulesPage,
  }),
  // The tenancy's own code — what is registered, and which build of it is live.
  // Authoring stays in the separate code-editor app; promotion is here, because
  // it needed no editor and had no surface in either app.
  createRoute({ getParentRoute: () => rootRoute, path: "/integrations", component: IntegrationsPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/alerts",
    component: AlertsPage,
    // `?tab=` opens one of the three lenses directly — the same convention the
    // address detail route uses. Destinations needed it most: they are where a
    // rule's "who gets told" comes from, and until they had an address the only
    // way to reach them was to already be on this page and know the tab existed.
    // An unknown value falls through to the page default rather than 404ing.
    validateSearch: (search: Record<string, unknown>): { tab?: string } => ({
      tab: str(search.tab),
    }),
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/watches", component: WatchesPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/watches/$id", validateSearch: (search: Record<string, unknown>): { scope?: string } => ({ scope: typeof search.scope === "string" ? search.scope : undefined }), component: WatchDetailPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/situations",
    component: SituationsPage,
    // `?kind=` and `?scope=` are the feed's lens, and it belongs in the URL for
    // the same reason the protocol catalogue's selection does — but here the
    // pressing reason is the back button. The page's whole shape is "scan the
    // feed, open one, come back, open the next", and with the filter in
    // component state every return from a situation landed on an unfiltered
    // feed and re-walked five pages of it. An unknown value falls through to
    // the page default rather than 404ing.
    validateSearch: (
      search: Record<string, unknown>,
    ): {
      kind?: string;
      shape?: string;
      scope?: string;
      window?: string;
      since?: number;
      until?: number;
    } => ({
      kind: str(search.kind),
      // `?shape=finding` — a CARD SHAPE, which is several kinds. Coarser than
      // `?kind=` and the one a reader starts from: "the accusations" rather
      // than "this detector's output".
      shape: str(search.shape),
      scope: str(search.scope),
      // The time window: a rolling preset (`?window=24h`) or an absolute
      // `?since=&until=` pair.
      //
      // The bounds are NUMBERS, not strings. The router JSON-encodes a search
      // value whose string form would be ambiguous with another type, so a
      // numeric-looking string is serialised as `?since=%221790114400%22` —
      // quotes and all — and the API then parses `"1790114400"`, fails, and
      // drops the window silently. `?window=24h` is unaffected because `24h`
      // is not numeric, which is exactly why this was invisible until an
      // absolute range was picked.
      window: str(search.window),
      since: num(search.since),
      until: num(search.until),
    }),
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/actors", component: ActorsPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/prediction-markets",
    component: PredictionMarketsPage,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/flows",
    component: FlowsPage,
    // The board's lens is the URL, so "every cash-out into KuCoin above 0.8" is
    // a link an investigator can paste into a case note, and survives a reload.
    // Unknown values are dropped here rather than breaking the page.
    validateSearch: (
      search: Record<string, unknown>,
    ): { sort?: "value" | "paths" | "recent"; ended?: string; min?: number; q?: string } => ({
      sort: search.sort === "paths" || search.sort === "recent" ? search.sort : undefined,
      ended: str(search.ended),
      min: num(search.min) !== undefined && num(search.min)! < 1 ? num(search.min) : undefined,
      q: str(search.q),
    }),
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/contracts", component: ContractsPage }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/contracts/$chainId/$address",
    component: ContractDetailPage,
    // `?block=` pins every read on the page to a block height. In the URL rather
    // than component state because a contract's ABI and its proxy implementation
    // are both block-ranged: "this contract at block N" is the shareable thing an
    // operator sends to a colleague, and without the pin in the link the colleague
    // opens the same page resolved at the chain head.
    //
    // `?tab=` names which of the explorer's thirteen tabs opens, and `?tx=` the
    // transaction this contract was opened FROM. Together they are the return
    // leg of the "make this transaction decode" round trip: a reader sent here
    // by a decode gap used to land on Fields, hunt for "Edit" along a wrapped
    // tab strip, and then have no way back to the transaction and no re-decode
    // action — so the workflow had a forward link and nothing else.
    //
    // `tx` is the bare 0x hash, deliberately: the chain is already in the route
    // (a transaction and the contract it called are on the same chain), and the
    // page composes both the `{slug}:{hash}` ref `/transactions/$id` wants and
    // the `?chain=&tx=` pair `/runs` validates. Shape-checked here so a
    // malformed value drops out instead of rendering a dead link.
    validateSearch: contractSearch,
  }),
  // Chain-scoped like a contract, and for the same reason: a pool exists on ONE
  // chain. `$address` is the pool's own contract (V2/V3) or a V4 pool id — hence
  // no address-shaped validation on it.
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/pools/$chainId/$address",
    component: PoolDetailPage,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: "/wallet", component: WalletPage }),
  createRoute({ getParentRoute: () => rootRoute, path: "/team", component: TeamPage }),
  // Team-invite acceptance landing (from the branded email). `?token=` is the
  // secret accept-token; the page POSTs it to `/teams/invites/accept`.
  createRoute({
    getParentRoute: () => rootRoute,
    path: "/teams/accept-invite",
    component: AcceptTeamInvitePage,
    validateSearch: (search: Record<string, unknown>): { token?: string } => ({
      token: typeof search.token === "string" && search.token ? search.token : undefined,
    }),
  }),
  settingsRouteTree,
  createRoute({ getParentRoute: () => rootRoute, path: "/profile", component: ProfilePage }),
];

const routeTree = rootRoute.addChildren(routes);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultPendingComponent: RoutePending,
  defaultPendingMs: 200,
  defaultPendingMinMs: 200,
  defaultErrorComponent: RouteError,
  // Keep `:` literal in path params so a chain-prefixed transaction id renders
  // as `/transactions/eth:0x…` (the form `transactions-mgmt-api` expects) rather
  // than the percent-encoded `eth%3A0x…`.
  pathParamsAllowedCharacters: [":"],
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
